import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { abrir, aparecer } from '../components/movimiento';
import { api, guardarToken, type ConfigAuth, type Perfil } from '../api';
import { mensajeDeError, useApi } from '../lib/useApi';
import { Cargando, Marca as MarcaComun } from '../components/ui';
import { aviso, botonSecundario, c } from '../theme';

/** Google Identity Services, cargado una sola vez cuando hace falta. */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (o: { client_id: string; callback: (r: { credential: string }) => void }) => void;
          renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const SRC_GIS = 'https://accounts.google.com/gsi/client';

function cargarGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existente = document.querySelector<HTMLScriptElement>(`script[src="${SRC_GIS}"]`);
    if (existente) {
      existente.addEventListener('load', () => resolve());
      existente.addEventListener('error', () => reject(new Error('gis')));
      return;
    }
    const s = document.createElement('script');
    s.src = SRC_GIS;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('gis'));
    document.head.append(s);
  });
}

function Marca() {
  return (
    <>
      <MarcaComun tamano={48} />

      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.03em', color: c.texto }}>
          Iniciá sesión
        </h1>
        {/* No se nombra qué cuentas tienen acceso: esta pantalla la ve
            cualquiera y sería decirle contra qué probar. */}
        <div style={{ marginTop: 6, fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
          Entrá con tu cuenta de Google corporativa.
        </div>
      </div>
    </>
  );
}

export default function Login({ onEntrar }: { onEntrar: (perfil: Perfil) => void }) {
  const { datos: conf, cargando, error: errorConf, recargar } = useApi<ConfigAuth>(() => api.configAuth(), []);
  const [error, setError] = useState('');
  const [entrando, setEntrando] = useState(false);
  const cajaBoton = useRef<HTMLDivElement>(null);

  // El callback de Google vive fuera de React, así que la referencia se
  // mantiene fresca con un ref en vez de recrear el botón en cada render.
  const alRecibir = useRef(async (credential: string) => {
    setEntrando(true);
    setError('');
    try {
      const { token, usuario } = await api.loginGoogle(credential);
      guardarToken(token);
      onEntrar(usuario);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setEntrando(false);
    }
  });

  useEffect(() => {
    const clientId = conf?.googleClientId;
    if (!clientId || !cajaBoton.current) return;

    let vivo = true;
    cargarGis()
      .then(() => {
        if (!vivo || !cajaBoton.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (r) => void alRecibir.current(r.credential),
        });
        window.google.accounts.id.renderButton(cajaBoton.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'center',
          // El botón de Google tiene ancho fijo: en 360 px la tarjeta mide menos de 340.
          width: Math.min(340, window.innerWidth - 80),
          locale: 'es',
        });
      })
      .catch(() => {
        if (vivo) setError('No pudimos cargar el acceso de Google. Revisá la conexión y recargá la página.');
      });

    return () => {
      vivo = false;
    };
  }, [conf?.googleClientId]);

  async function entrarDemo() {
    setEntrando(true);
    setError('');
    try {
      const { token, usuario } = await api.loginDemo();
      guardarToken(token);
      onEntrar(usuario);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setEntrando(false);
    }
  }

  const caja: React.CSSProperties = {
    width: '100%',
    maxWidth: 420,
    background: c.panel,
    border: `1px solid ${c.borde}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '32px 16px', background: c.fondo }}>
      <motion.div {...abrir(16)} className="p-6 tablet:p-8" style={caja}>
        <Marca />

        {cargando && !conf && <Cargando texto="Preparando el acceso…" alto={120} />}

        <AnimatePresence>
          {errorConf && (
            <motion.div {...abrir(10)} style={{ ...aviso('err'), display: 'flex', flexDirection: 'column', gap: 10 }} role="alert">
              {errorConf}
              <button type="button" onClick={recargar} style={{ ...botonSecundario, alignSelf: 'flex-start' }}>
                Reintentar
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {error && (
            <motion.div key={error} {...abrir(10)} style={aviso('err')} role="alert">
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {conf?.googleClientId && (
          // Entra después de la tarjeta, abriéndose desde el centro.
          <motion.div
            {...aparecer}
            animate={{ ...aparecer.animate, opacity: entrando ? 0.6 : 1 }}
            transition={{ ...aparecer.transition, delay: 0.2 }}
            style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}
          >
            <div ref={cajaBoton} />
          </motion.div>
        )}

        {conf && !conf.googleClientId && !conf.loginDemo && (
          <div style={aviso('warn')}>El ingreso con Google no está configurado. Avisale a sistemas.</div>
        )}

        {conf?.loginDemo && (
          <motion.button
            type="button"
            onClick={entrarDemo}
            disabled={entrando}
            {...abrir(999)}
            transition={{ ...abrir(999).transition, delay: 0.3 }}
            style={{ ...botonSecundario, minHeight: 48, justifyContent: 'center' }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={entrando ? 'a' : 'b'}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.14 }}
              >
                {entrando ? 'Entrando…' : 'Entrar con la cuenta de prueba'}
              </motion.span>
            </AnimatePresence>
          </motion.button>
        )}

        <div style={{ fontSize: 12, color: c.texto3, textWrap: 'pretty' }}>
          ¿No podés entrar? Pedile acceso al administrador del depósito.
        </div>
      </motion.div>
    </div>
  );
}
